import { Sparkles, Wand2 } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { PageHeader } from "@/shared/ui/PageHeader";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import { Pagination } from "@/shared/ui/pagination";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";

import { dhResolutionLabel } from "../model/dhCreateOptions";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL, CARD_WALL_CAP, CARD_WALL_RADIUS, CARD_WALL_TOUCH } from "@/shared/ui/cardWall";
import { DH_LINE_ROUTE } from "@/entities/dh-profile/model/dhLines";
import { dhShowcaseService, toRecipe, type DhShowcaseDto } from "../api/dhShowcaseService";
import { errorText } from "@/shared/lib/apiErrors";

// B2 #12:清晰度档名走 dhCreateOptions.dhResolutionLabel 单源(此前本地表「480P→标清 / 720P→高清」
// 停在万相时代,与创建页「高清 720 / 超清 1080」两套名字对不上)。

/**
 * 数字人样片墙(S9-D,租户只读)。平台策展的样片橱窗:每张卡展示预置形象 + 音色 + 文案框架,「一键套用」把 recipe
 * 预填到创作页(全 preset 安全资产),租户可原样出片或在创作页再换成自己的声纹/照片。
 *
 * 2026-07-25 G1 铺量批:上卡墙规格。
 *  - 栅格从视口断点(`sm:2 lg:3`)改**容器查询** `cardwall-cap`:实测原版视口 780 处容器 476
 *    却已越过 `sm` → 2 列 **230px**;而 1024 以上恒 3 列,1920 下卡宽撑到 **528px**
 *    (一张卡宽过两张)。列宽取决于容器不取决于视口,判据也必须量容器。
 *  - 走 cap 档(250~365)不走 media 档:卡面是「播放器 + 标题 + 三枚标 + 描述 + 台词 + 整行按钮」,
 *    内容量比场景卡还大,150~260 会把台词挤成一列窄条。
 *  - 圆角走形状锁单源;加载态换成按最终卡形状的骨架屏(原来是一个居中转圈,不告诉用户会出现什么)。
 */
export default function DhShowcaseGalleryPage() {
  const navigate = useNavigate();

  // #7 服务端真分页;内部 page 0-based,后端 gallery 1-based → 传 page+1。
  const PAGE_SIZE = 12;
  const [page, setPage] = useState(0);

  const galleryQuery = useQuery({
    queryKey: ["dh", "showcase", "gallery", page],
    queryFn: () => dhShowcaseService.gallery({ page: page + 1, size: PAGE_SIZE }),
  });
  const items = galleryQuery.data?.records ?? [];
  const gridRef = useRef<HTMLDivElement>(null);
  useStaggerReveal(gridRef, { deps: [items.length] });
  const total = galleryQuery.data?.total ?? 0;

  const apply = (s: DhShowcaseDto) => {
    // recipe 走导航 state 预填创作页;全 preset,不碰租户私有资产。
    // ★直指照片线:recipe.avatarId 是**预置形象**,而预置形象库是照片资产 ——
    //   真人口播线的形象弹框里根本没有「预置形象」这个来源 tab(见 DhAvatarPicker)。
    navigate(DH_LINE_ROUTE.photo, { state: { recipe: toRecipe(s) } });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/ai-digital-human"
        icon={Sparkles}
        title="分身样片墙"
        subtitle="平台精选样片,一键套用即把形象 / 音色 / 文案预填到创作页,可直接出片,也可换成自己的声纹或照片。"
        actions={
          <Button variant="outline" onClick={() => navigate("/ai-digital-human")}>
            我的口播视频
          </Button>
        }
      />

      <QueryErrorBanner
        isError={galleryQuery.isError}
        message={galleryQuery.error ? errorText(galleryQuery.error) : undefined}
        onRetry={() => galleryQuery.refetch()}
      />

      {galleryQuery.isLoading ? (
        <div className={CARD_WALL}>
          <div className={CARD_WALL_CAP} data-testid="dh-showcase-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={cn("fx-skeleton border border-border/50 bg-card/40", CARD_WALL_RADIUS)}
                // 骨架高度 = 16:9 播放器 + 五行文案区,按最终卡形状给
                style={{ aspectRatio: "16 / 19" }}
              />
            ))}
          </div>
        </div>
      ) : galleryQuery.isError ? null : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center text-sm text-muted-foreground">
            <p>样片墙还在准备中,先去创作一条本人口播视频吧。</p>
            {/* 文案说的就是「本人口播视频」→ 直指真人口播线。 */}
            <Button onClick={() => navigate(DH_LINE_ROUTE.talking)}>去创作</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className={CARD_WALL}>
            <div ref={gridRef} className={CARD_WALL_CAP}>
              {items.map((s) => (
                <Card
                  key={s.id}
                  data-showcase-card={s.id}
                  className={cn("fx-hover-lift flex flex-col overflow-hidden", CARD_WALL_RADIUS)}
                >
                  <div className="aspect-video w-full bg-muted">
                    {s.previewUrl ? (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <video
                        src={s.previewUrl}
                        controls
                        preload="metadata"
                        className="h-full w-full object-cover"
                        aria-label={`${s.title} 样片预览`}
                      />
                    ) : s.avatarThumbUrl ? (
                      <img
                        src={s.avatarThumbUrl}
                        alt={`${s.title} 形象`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                        暂无预览
                      </div>
                    )}
                  </div>
                  <CardContent className="flex flex-1 flex-col gap-2 p-4">
                    <div className="text-sm font-medium">{s.title}</div>
                    <div className="flex flex-wrap gap-1.5 text-2xs text-muted-foreground">
                      <span className="rounded bg-muted px-1.5 py-0.5">{s.avatarLabel}</span>
                      <span className="rounded bg-muted px-1.5 py-0.5">{s.voiceLabel}</span>
                      <span className="rounded bg-muted px-1.5 py-0.5">
                        {dhResolutionLabel(s.resolution)}
                      </span>
                    </div>
                    {s.description && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">{s.description}</p>
                    )}
                    <p className="line-clamp-2 text-xs text-muted-foreground/80">{s.script}</p>
                    <div className="mt-auto pt-1">
                      <Button
                        size="sm"
                        className={cn("w-full", CARD_WALL_TOUCH)}
                        onClick={() => apply(s)}
                      >
                        <Wand2 className="mr-2 h-4 w-4" />
                        一键套用
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          {total > PAGE_SIZE && (
            <Pagination
              className="mt-4"
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}
